import { FC, useEffect, useRef, useState } from 'react';

import './UpdatePrompt.css';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button, Meta, Notice } from '../../ui';

/** How often an open till re-checks for a newer build. */
const UPDATE_INTERVAL_MS = 60 * 1000;

/**
 * Tell the operator that a newer build has been installed and offer a reload.
 *
 * The service worker registers with `registerType: 'prompt'` (see vite.config.ts),
 * so a fresh worker waits instead of swapping the app out mid-sale. A till can
 * stay open all day — well past the browser's next navigation — so updates are
 * also polled on a timer and whenever the tab regains focus.
 */
const UpdatePrompt: FC = () => {
  const timer = useRef<number | null>(null);
  const poke = useRef<(() => void) | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const check = () => {
        if (document.visibilityState === 'visible') void registration.update();
      };
      poke.current = check;
      timer.current = window.setInterval(check, UPDATE_INTERVAL_MS);
      // A till can sit on one screen for hours; also re-check when the window
      // is brought back to the foreground or the network returns.
      window.addEventListener('focus', check);
      window.addEventListener('online', check);
      document.addEventListener('visibilitychange', check);
    },
  });

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearInterval(timer.current);
      if (poke.current) {
        window.removeEventListener('focus', poke.current);
        window.removeEventListener('online', poke.current);
        document.removeEventListener('visibilitychange', poke.current);
      }
    },
    []
  );

  /*
   * A newly found build overrides an earlier "later".
   *
   * Adjusted during render rather than in an effect: this is derived state, and
   * setting it from an effect body would cascade a second render for a value the
   * current render already knows. React re-runs this component before committing,
   * so no half-updated frame is shown.
   */
  const [lastNeedRefresh, setLastNeedRefresh] = useState(needRefresh);
  if (needRefresh !== lastNeedRefresh) {
    setLastNeedRefresh(needRefresh);
    if (needRefresh) setDismissed(false);
  }

  if (!needRefresh || dismissed) return null;

  return (
    <Notice variant="info" className="updatePrompt">
      <Meta as="span">A new version is available.</Meta>
      <Button
        variant="primary"
        size="sm"
        onClick={() => {
          setNeedRefresh(false);
          void updateServiceWorker(true);
        }}
      >
        refresh
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setDismissed(true)}>
        later
      </Button>
    </Notice>
  );
};

export default UpdatePrompt;
